import { Button, Spinner, useLoadingTimeout, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PostMinimal } from '../posts';
import { usePhotoUrl } from '../posts';
import type { SessionItem } from '../sessions';
import { InsightDetailModal } from './InsightDetailModal';
import { sentimentToBgGradient, sentimentToHsl } from './sentimentColor';
import type { IdeaMinimal } from './useEcwIdeas';
import { useEcwInsights, type InsightMinimal } from './useEcwInsights';
import { useEcwOutbox } from './useEcwOutbox';

/**
 * docs/107 Wave8 / old2 InsightList.tsx:381-393 — ある気づきに紐づく処方箋が存在するか。
 * idea.insightIds に当該 insight.id を含む、または insight が processing 由来の virtual_${idea.id}。
 */
function hasRelatedIdeasFor(insightId: string, ideas: IdeaMinimal[]): boolean {
  return ideas.some(
    (idea) => idea.insightIds.includes(insightId) || insightId === `virtual_${idea.id}`,
  );
}

/** docs/107 Wave8 / old2 InsightList.tsx:371-378 — 一覧カードの症状/診断は 60 字でトリム。 */
function trim60(s: string): string {
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

/** Task C: outbox 由来の楽観 UI 表示用 internal alias (型汚染回避)。 */
type InsightWithOutbox = InsightMinimal & { _pendingOutbox?: boolean };

/**
 * 分析アプリ「気づき」タブ (Sprint X5c minimum、REQ-4.2.1 / R-38 #3、旧 `InsightList.tsx` 409 行から narrow 移植)。
 *
 * minimum 仕様 (旧 InsightList の核):
 *   - 一覧表示 (sentiment 降順)
 *   - 作成 modal (title / observations / diagnosis / sentiment slider / selectedPostIds)
 *   - 削除 button (作成者本人のみ表示、rules で enforce)
 *   - sentiment に基づくカード枠色 (HSL 0=赤 → 240=青 グラデーション、旧 L76-87 踏襲)
 *
 * 旧 InsightList との差分:
 *   - AI 対話 (Step1-2 + llmAnalysis) は scope 外 (Sprint X5d で M4 連動)
 *   - 詳細 modal の MapLibre 地図 + selectedPosts 写真 grid は scope 外 (X5d)
 *   - チーム filter は scope 外 (旧 teamFilter prop)
 *   - 一覧の sentiment ソート + onlyMine filter のみ (旧の検証カード連携など省略)
 */

export interface AnalysisInsightViewProps {
  districtId: string;
  uid: string | null;
  /** R-70: 「自分の気づき」とみなす全 uid (現 uid + 端末引き継ぎ元)。省略時は uid 単独。 */
  selfUids?: string[];
  nickname: string;
  posts: PostMinimal[];
  /** docs/107 Wave8: 処方箋一覧 (カード/詳細の「関連する処方箋」導線の有無判定 + 遷移に使用)。 */
  ideas?: IdeaMinimal[];
  onSelectPost?: (post: PostMinimal) => void;
  /**
   * 当該地区の全セッション (id+name)。カードのセッション名表示 + セッションフィルタの
   * 有効 ID 判定に使う。未指定ならセッション表示/絞込なし。
   */
  sessions?: SessionItem[];
  /**
   * ヘッダー右上のセッションフィルタの選択集合。気づきも投稿と同じ規則で絞り込む
   * (未紐付け / 孤児 ID は常に表示、空集合は「絞込なし」)。未指定なら絞込なし。
   */
  selectedSessionIds?: Set<string>;
  /** M4 aiDialogueSettings ID (Sprint X5g、未指定なら callable 側で 'ecw-pilot' fallback)。 */
  aiDialogueSettingsId?: string | null;
  /**
   * Sprint BG-DataSettings 23 — Matrix / List → 気づき WS の handoff。
   * Phase B: 受信時に親 (AnalysisPage) が `screen='ideaForm' mode='front'` に切替えるため、
   * 本 view は prefill 内容を消費するだけで workspace を直接開かない。
   */
  prefilledPostIds?: string[];
  onPrefillConsumed?: () => void;
  /**
   * Sprint BG-DataSettings 23 — InsightCard 「→ 処方箋作成」 click 時に呼ばれる。
   * AnalysisPage 側で 'ideas' tab に switch + prefilledInsight を set する。
   */
  onProceedToIdea?: (insight: InsightMinimal) => void;
  /**
   * Phase B: 「💡 気づきを作る (対話モード)」 button 押下時、親 AnalysisPage が
   * `screen='ideaForm' mode='front'` に切替える。
   */
  onStartFrontWorkspace?: (postIds: string[]) => void;
  /**
   * Phase B: 複数 insight を選択して「💊 処方箋をつくる(N件)」 から
   * `screen='ideaForm' mode='back'` に切替える (selectedInsights を AnalysisPage が保持)。
   */
  onStartBackWorkspace?: (insights: InsightMinimal[]) => void;
}

// Sprint AW: sentiment→HSL 変換は sentimentColor.ts に共通化 (旧 InsightList L75-87 踏襲)。

/** Sprint AW: card 一覧の並び順。 */
type InsightSortKey = 'sentiment-desc' | 'sentiment-asc' | 'newest' | 'oldest';

const SORT_LABELS: Record<InsightSortKey, string> = {
  'sentiment-desc': 'sentiment ↓ (ポジ→ネガ)',
  'sentiment-asc': 'sentiment ↑ (ネガ→ポジ)',
  newest: '新しい順',
  oldest: '古い順',
};

export function AnalysisInsightView({
  districtId,
  uid,
  selfUids,
  nickname: _nickname,
  posts,
  ideas = [],
  onSelectPost,
  sessions,
  selectedSessionIds,
  aiDialogueSettingsId: _aiDialogueSettingsId,
  prefilledPostIds,
  onPrefillConsumed,
  onProceedToIdea,
  onStartFrontWorkspace,
  onStartBackWorkspace,
}: AnalysisInsightViewProps) {
  // Sprint AM: 30s timeout 後の retry 用 key (UI2 無限スピナー解消)
  const [retryKey, setRetryKey] = useState(0);
  const { state, deleteInsight } = useEcwInsights(districtId, retryKey);
  const loadTimedOut = useLoadingTimeout(state.status === 'loading');
  const { toast } = useToast();
  // Task C: outbox (オフライン保留) を 一覧上部に prepend。クリック不可 + 「📤 未送信」 badge。
  const { state: ecwOutboxState } = useEcwOutbox();
  const [onlyMine, setOnlyMine] = useState(false);
  const [sortKey, setSortKey] = useState<InsightSortKey>('sentiment-desc');
  const [detailInsight, setDetailInsight] = useState<InsightMinimal | null>(null);
  /** Phase B+1.3: 写真サムネサイズ slider (旧 InsightList L43-44、32-120px)。 */
  const [imageSize, setImageSize] = useState<number>(48);
  /** Phase B: 複数 insight 選択 → back モード処方箋ワークスペースへ。 */
  const [selectedInsightIds, setSelectedInsightIds] = useState<Set<string>>(new Set());

  // Phase B: prefilledPostIds が来たら親 AnalysisPage に front workspace 起動を依頼する。
  // 1 度だけ消費するため lastSigRef で抑止。
  const lastPrefillSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (prefilledPostIds && prefilledPostIds.length > 0) {
      const sig = prefilledPostIds.join(',');
      if (lastPrefillSigRef.current !== sig) {
        lastPrefillSigRef.current = sig;
        onStartFrontWorkspace?.(prefilledPostIds);
        onPrefillConsumed?.();
      }
    }
  }, [prefilledPostIds, onStartFrontWorkspace, onPrefillConsumed]);

  // セッション名解決 (カード表示) + 有効セッション ID 集合 (フィルタの孤児判定)。
  const sessionNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sessions ?? []) m.set(s.id, s.name);
    return m;
  }, [sessions]);
  const validSessionIds = useMemo(() => new Set((sessions ?? []).map((s) => s.id)), [sessions]);

  // Task C: outbox 由来の insight を InsightMinimal 互換 shim に変換 (districtId が一致するもののみ)
  const outboxAsInsights: InsightWithOutbox[] = useMemo(
    () =>
      ecwOutboxState.insights
        .filter((o) => o.districtId === districtId)
        .map((o) => ({
          id: `outbox:${o.id ?? 'unknown'}`,
          districtId: o.districtId,
          userId: o.userId,
          nickname: o.nickname,
          title: o.title,
          observations: o.observations,
          diagnosis: o.diagnosis,
          sentiment: o.sentiment,
          selectedPostIds: o.selectedPostIds,
          hidden: false,
          createdAt: null,
          initialThoughts: o.initialThoughts,
          llmAnalysis: o.llmAnalysis,
          llmResolutionQuestions: o.llmResolutionQuestions,
          llmDiagnosisQuestions: o.llmDiagnosisQuestions,
          emotionTag: o.emotionTag,
          categoryLabel: o.categoryLabel,
          team: o.team,
          sessionId: o.sessionId,
          _pendingOutbox: true,
        })),
    [ecwOutboxState.insights, districtId],
  );

  // R-70: 「自分」= 現 uid + 端末引き継ぎ元 (selfUids)。省略時は uid 単独 (後方互換)。
  const ownUids = useMemo(
    () => (selfUids && selfUids.length > 0 ? selfUids : uid ? [uid] : []),
    [selfUids, uid],
  );

  const visible = useMemo<InsightWithOutbox[]>(() => {
    // 投稿のセッションフィルタと同じ規則: 絞込なし(空)は全件、未紐付け/孤児 ID は常に表示。
    const sessionOk = (sid: string | null | undefined): boolean => {
      if (validSessionIds.size === 0 || !selectedSessionIds || selectedSessionIds.size === 0) {
        return true;
      }
      if (!sid) return true;
      if (!validSessionIds.has(sid)) return true;
      return selectedSessionIds.has(sid);
    };
    let list: InsightWithOutbox[] = state.items.filter((it) => sessionOk(it.sessionId));
    if (onlyMine && ownUids.length > 0) list = list.filter((it) => ownUids.includes(it.userId));
    const sorted = [...list];
    // Sprint AW: sentiment / 作成日時 ソート (旧 InsightList L57-66 踏襲、UI に切替 select 追加)
    if (sortKey === 'sentiment-desc') {
      sorted.sort((a, b) => b.sentiment - a.sentiment);
    } else if (sortKey === 'sentiment-asc') {
      sorted.sort((a, b) => a.sentiment - b.sentiment);
    } else {
      // newest / oldest: createdAt が null の場合 0 fallback
      sorted.sort((a, b) => {
        const av = a.createdAt?.toMillis?.() ?? 0;
        const bv = b.createdAt?.toMillis?.() ?? 0;
        return sortKey === 'newest' ? bv - av : av - bv;
      });
    }
    // Task C: outbox 由来を先頭に prepend (onlyMine filter は uid 一致なら通る、そうでなければ除外)
    let outboxFiltered = outboxAsInsights.filter((o) => sessionOk(o.sessionId));
    if (onlyMine && ownUids.length > 0) {
      outboxFiltered = outboxFiltered.filter((o) => ownUids.includes(o.userId));
    }
    return [...outboxFiltered, ...sorted];
  }, [
    state.items,
    onlyMine,
    ownUids,
    sortKey,
    outboxAsInsights,
    validSessionIds,
    selectedSessionIds,
  ]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-700">💡 気づき一覧</h2>
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
            disabled={!uid}
          />
          自分の気づきのみ表示
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          {/* Phase B+1.3: 写真サムネサイズ slider (旧 InsightList L276-285) */}
          <span>写真サイズ:</span>
          <input
            type="range"
            min={32}
            max={120}
            value={imageSize}
            onChange={(e) => setImageSize(Number(e.target.value))}
            className="w-20 cursor-pointer"
            aria-label="気づきカードの写真サイズ"
          />
          <span className="tabular-nums w-7 text-right">{imageSize}</span>
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-1.5">
          並び順:
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as InsightSortKey)}
            className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white"
            aria-label="気づきカードの並び順"
          >
            {(Object.keys(SORT_LABELS) as InsightSortKey[]).map((k) => (
              <option key={k} value={k}>
                {SORT_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs text-slate-500 ml-auto">
          {state.status === 'loading' ? '読込中…' : `${visible.length} 件`}
        </span>
        {/* 気づきは「マトリクス/地図 → 分析 → 気づきの記入へ」の一本道でのみ作成する。
            投稿を踏まえないゼロからの作成 (シンプル作成 / 投稿なし対話) は廃止 (ユーザー指示)。
            ここで選んだ気づきを束ねて処方箋を作る導線のみ残す。 */}
        {onStartBackWorkspace && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              const picked = state.items.filter((it) => selectedInsightIds.has(it.id));
              if (picked.length === 0) return;
              onStartBackWorkspace(picked);
              setSelectedInsightIds(new Set());
            }}
            disabled={!uid || selectedInsightIds.size === 0}
          >
            💊 処方箋をつくる ({selectedInsightIds.size}件)
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* Sprint AM: Spinner + 30s timeout 後 retry banner (UI2 無限スピナー解消) */}
        {state.status === 'loading' && !loadTimedOut && <Spinner label="読込中…" />}
        {state.status === 'loading' && loadTimedOut && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-warning-700">読込が遅れています。</span>
            <Button variant="secondary" size="sm" onClick={() => setRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}
        {state.status === 'error' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-red-600">読込エラー: {state.error.message}</span>
            <Button variant="secondary" size="sm" onClick={() => setRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}
        {state.status === 'ready' && visible.length === 0 && (
          <p className="text-sm text-slate-500">
            {onlyMine ? '自分の気づきはまだありません。' : 'まだ気づきがありません。'}
            <br />
            「マトリクス」または「地図」で投稿を選び、「分析」タブの「気づきの記入へ」から作成してください。
          </p>
        )}
        {/*
          画面幅に応じた段組み (2026-09-05): 以前は `space-y-3` の 1 列積みで、カードが
          ブラウザ幅いっぱい (4K なら 1 枚 3840px) に伸びていた。スマホ 1 列 / タブレット 2 列 /
          PC 3 列 / 超広画面 4 列。`grid-cols-1` を明示するのは、素の `grid gap-*` だと列トラックの
          最小幅が中身の min-content になり長いテキストで右が切れるため (CLAUDE.md の既知の落とし穴)。
        */}
        <ul
          className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4"
          data-testid="insight-card-list"
        >
          {visible.map((it) => {
            const isOutbox = it._pendingOutbox === true;
            return (
              <InsightCard
                key={it.id}
                insight={it}
                isOwner={!isOutbox && ownUids.includes(it.userId)}
                sessionName={it.sessionId ? (sessionNameById.get(it.sessionId) ?? null) : null}
                posts={posts}
                imageSize={imageSize}
                pendingOutbox={isOutbox}
                onSelectPost={isOutbox ? undefined : onSelectPost}
                onShowDetail={
                  isOutbox
                    ? () => toast('warning', '未送信のため詳細を表示できません')
                    : () => setDetailInsight(it)
                }
                onProceedToIdea={
                  !isOutbox && onProceedToIdea ? () => onProceedToIdea(it) : undefined
                }
                hasRelatedIdeas={!isOutbox && hasRelatedIdeasFor(it.id, ideas)}
                multiSelectEnabled={!isOutbox && Boolean(onStartBackWorkspace)}
                isSelected={selectedInsightIds.has(it.id)}
                onToggleSelected={() => {
                  const next = new Set(selectedInsightIds);
                  if (next.has(it.id)) next.delete(it.id);
                  else next.add(it.id);
                  setSelectedInsightIds(next);
                }}
                onDelete={async () => {
                  if (!confirm(`「${it.title}」を削除しますか?`)) return;
                  try {
                    await deleteInsight(it.id);
                    toast('success', '削除しました');
                  } catch (e) {
                    toast('error', `削除失敗: ${(e as Error).message}`);
                  }
                }}
              />
            );
          })}
        </ul>
      </div>

      <InsightDetailModal
        open={detailInsight !== null}
        onClose={() => setDetailInsight(null)}
        insight={detailInsight}
        posts={posts}
        ideas={ideas}
        districtId={districtId}
        sessionName={
          detailInsight?.sessionId ? (sessionNameById.get(detailInsight.sessionId) ?? null) : null
        }
        onShowRelatedIdeas={
          onProceedToIdea
            ? (ins) => {
                setDetailInsight(null);
                onProceedToIdea(ins);
              }
            : undefined
        }
        onSelectPost={(p) => {
          setDetailInsight(null);
          onSelectPost?.(p);
        }}
      />

      <p className="px-4 py-2 text-[10px] text-slate-400 shrink-0 border-t border-slate-100">
        ※ 気づきは「マトリクス/地図」で投稿を選び→「分析」→「気づきの記入へ」から AI
        対話で作成します。
      </p>
    </div>
  );
}

interface InsightCardProps {
  insight: InsightMinimal;
  isOwner: boolean;
  /** sessionId から解決したセッション名 (内部 ID は出さない)。未解決/無紐付けは null。 */
  sessionName?: string | null;
  posts: PostMinimal[];
  /** Phase B+1.3: 写真サムネサイズ (slider 制御値、32-120)。 */
  imageSize: number;
  onSelectPost?: (post: PostMinimal) => void;
  onShowDetail: () => void;
  onDelete: () => Promise<void>;
  /** 関連する処方箋一覧へ遷移 (old2 onShowRelatedIdeas、Sprint BG-DataSettings 23)。 */
  onProceedToIdea?: () => void;
  /** docs/107 Wave8: この気づきに紐づく処方箋が存在するか (footer 導線の表示ゲート)。 */
  hasRelatedIdeas?: boolean;
  /** Phase B: back-workspace 起動用 multi-select。 */
  multiSelectEnabled?: boolean;
  isSelected?: boolean;
  onToggleSelected?: () => void;
  /** Task C: outbox 由来 (オフライン未送信)。badge + disabled 状態にする。 */
  pendingOutbox?: boolean;
}

function InsightCard({
  insight,
  isOwner,
  sessionName,
  posts,
  imageSize,
  onSelectPost,
  onShowDetail,
  onDelete,
  onProceedToIdea,
  hasRelatedIdeas,
  multiSelectEnabled,
  isSelected,
  onToggleSelected,
  pendingOutbox,
}: InsightCardProps) {
  const linkedPosts = useMemo(
    () => posts.filter((p) => insight.selectedPostIds.includes(p.id)),
    [posts, insight.selectedPostIds],
  );
  return (
    <li
      // docs/107 Wave8 / old2 InsightList.tsx:320-328 踏襲。border-left を太く + sentiment 駆動の border 色。
      // 背景 gradient + ring 強調は **選択時のみ** (multi-select で選んだ気づきを際立たせる)。未選択は白背景。
      // Task C: outbox 由来は opacity-70 + 専用 border 色で識別。
      className={`rounded-lg border-2 border-l-4 p-3 shadow-sm relative ${
        pendingOutbox ? 'opacity-70' : ''
      }`}
      data-testid="insight-card"
      data-sentiment={insight.sentiment}
      data-pending-outbox={pendingOutbox ? 'true' : undefined}
      style={{
        borderColor: pendingOutbox ? '#f59e0b' : sentimentToHsl(insight.sentiment),
        background: isSelected ? sentimentToBgGradient(insight.sentiment) : 'white',
        boxShadow: isSelected
          ? `0 0 0 2px ${sentimentToHsl(insight.sentiment)}, 0 1px 3px rgba(0,0,0,0.12)`
          : undefined,
      }}
    >
      {pendingOutbox && (
        <span
          aria-label="未送信"
          className="absolute top-1 left-1 text-[10px] bg-warning-300 text-warning-800 px-1.5 py-0.5 rounded"
        >
          📤 未送信
        </span>
      )}
      <div className="flex items-start gap-2">
        {multiSelectEnabled && (
          <label
            className="flex items-center pt-0.5 cursor-pointer"
            aria-label="この気づきを処方箋ワークスペースの対象に含める"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected ?? false}
              onChange={onToggleSelected}
              className="w-4 h-4"
            />
          </label>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-slate-800 break-words">{insight.title}</h3>
          {/* docs/107 Wave8 / old2 InsightList.tsx:336-345 — 感情/対象分類タグ chip。 */}
          {(insight.emotionTag || insight.categoryLabel) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {insight.emotionTag && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                  {insight.emotionTag}
                </span>
              )}
              {insight.categoryLabel && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                  {insight.categoryLabel}
                </span>
              )}
            </div>
          )}
          {/* old2 InsightList.tsx:355 — セッション名 / 検討チーム + nickname。sentiment 生スコアは非表示。
              セッション名は sessionId から解決 (新方式)、team は旧データの fallback として併記。 */}
          <p className="text-[11px] text-slate-500 mt-0.5">
            {sessionName ? `セッション: ${sessionName} · ` : ''}
            {insight.team ? `検討チーム: ${insight.team} · ` : ''}by {insight.nickname || '匿名'}
          </p>
        </div>
        <button
          type="button"
          onClick={onShowDetail}
          aria-label="気づきカード詳細を表示"
          className="text-primary-600 hover:text-primary-800 text-xs px-1.5 py-0.5 rounded border border-primary-300 bg-white/70 transition-colors"
        >
          詳細
        </button>
        {isOwner && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="気づきカードを削除"
            className="text-slate-400 hover:text-red-600 text-xs px-1.5 py-0.5 rounded transition-colors"
          >
            削除
          </button>
        )}
      </div>
      {/* Phase B+1.3: 写真サムネ横スクロール (旧 InsightList L358-369、imageSize slider 連動) */}
      {linkedPosts.length > 0 && (
        <div
          className="mt-2 flex gap-1 overflow-x-auto pb-1"
          aria-label={`紐づく投稿写真 ${linkedPosts.length} 件`}
        >
          {linkedPosts.map((p) => (
            <SizedPostThumb key={p.id} post={p} size={imageSize} />
          ))}
        </div>
      )}
      {/* docs/107 Wave8 / old2 InsightList.tsx:371-378 — 一覧は症状/診断を 60 字トリム (全文は詳細 modal)。 */}
      {insight.observations && (
        <p className="mt-2 text-xs text-slate-700">
          <span className="font-semibold text-slate-600">症状: </span>
          {trim60(insight.observations)}
        </p>
      )}
      {insight.diagnosis && (
        <p className="mt-1 text-xs text-slate-700">
          <span className="font-semibold text-slate-600">診断: </span>
          {trim60(insight.diagnosis)}
        </p>
      )}
      {linkedPosts.length > 0 && (
        <div className="mt-2">
          <span className="text-[10px] font-semibold text-slate-600">
            紐づく投稿 ({linkedPosts.length})
          </span>
          <ul className="mt-1 flex flex-wrap gap-1">
            {linkedPosts.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="text-[10px] bg-white/70 hover:bg-white border border-slate-300 rounded px-1.5 py-0.5 text-slate-700 max-w-[200px] truncate"
                  onClick={() => onSelectPost?.(p)}
                  title={p.title}
                >
                  {p.title || '(無題)'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* docs/107 Wave8 / old2 InsightList.tsx:381-393 — 関連する既存処方箋がある場合のみ表示。
          押下で ideas タブへ遷移しこの気づきで filter (= 既存処方箋を見る導線。新規作成導線ではない)。 */}
      {onProceedToIdea && hasRelatedIdeas && (
        <div className="mt-3 pt-2 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onProceedToIdea}
            className="text-xs px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            aria-label="この気づきに紐づく処方箋を見る"
          >
            🔗 関連する処方箋
          </button>
        </div>
      )}
    </li>
  );
}

/* ============================================================================
 * Phase B+1.3 — 可変サイズ post thumb (旧 InsightList L358-369 imageSize slider 連動)。
 *
 * `PostThumb` は固定 60x60 設計で imageSize 連動できないため、本 view 専用に
 * 簡易版 thumb を再構築する (旧 InsightList が直接 <img> で実装していた挙動を踏襲)。
 * 写真未取得 / 失敗時は 📷 placeholder。
 * ========================================================================== */

function SizedPostThumb({ post, size }: { post: PostMinimal; size: number }) {
  // docs/106: doc 保存済 thumb URL を渡して cache miss 時の楽観表示 + オフライン fallback。
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl);
  // 旧 InsightList L365: width = max(imageSize * 1.3, 40) で横長サムネ
  const width = Math.max(Math.round(size * 1.3), 40);
  // CSS custom property 経由で動的サイズ (CLAUDE.md design 原則 OK ルール内)
  const style = {
    width: `${width}px`,
    height: `${size}px`,
    flexShrink: 0,
  } as React.CSSProperties;
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className="object-cover rounded border border-slate-300 bg-slate-100"
        style={style}
        title={post.title || ''}
      />
    );
  }
  return (
    <span
      aria-label={photo.status === 'failed' ? '写真の読込に失敗' : '写真読込中'}
      className={`flex items-center justify-center rounded border border-slate-300 bg-slate-100 text-slate-400 text-sm ${photo.status === 'loading' ? 'animate-pulse' : ''}`}
      style={style}
    >
      📷
    </span>
  );
}
